// src/components/admin/JobDetailsView.js
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Container,
  Box,
  Typography,
  Paper,
  Button,
  IconButton,
  AppBar,
  Toolbar,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Grid,
  Divider,
  List,
  ListItem,
  ListItemText,
  Chip,
  Menu,
  MenuItem,
  ListItemIcon,
} from "@mui/material";
import {
  ArrowBack as BackIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  AssignmentInd as AssignIcon,
  GetApp as DownloadIcon,
  Visibility as ViewIcon,
  MoreVert as MoreVertIcon,
} from "@mui/icons-material";
import { format } from "date-fns";

const JobDetailsView = () => {
  const { id: jobId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [job, setJob] = useState(null);
  const [equipment, setEquipment] = useState([]);
  const [error, setError] = useState("");
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [selectedEquipment, setSelectedEquipment] = useState(null);

  useEffect(() => {
    fetchJobDetails();
  }, [jobId]);

  const fetchJobDetails = async () => {
    try {
      setLoading(true);
      // Fetch job details
      const jobRes = await axios.get(`/jobs/${jobId}`);
      setJob(jobRes.data.job);

      // Fetch equipment for this job
      const equipmentRes = await axios.get(`/equipment/job/${jobId}`);
      setEquipment(equipmentRes.data.equipment);

      setError("");
    } catch (error) {
      setError("Failed to load job details. Please try again.");
      console.error("Error fetching job details:", error);
    } finally {
      setLoading(false);
    }
  };

  const goBack = () => {
    navigate("/admin"); // Back to admin dashboard
  };

  const handleEditJob = () => {
    navigate(`/admin/job/${jobId}`); // Navigate to job edit form
  };

  const openEquipmentMenu = (event, equipment) => {
    setMenuAnchorEl(event.currentTarget);
    setSelectedEquipment(equipment);
  };

  const closeEquipmentMenu = () => {
    setMenuAnchorEl(null);
    setSelectedEquipment(null);
  };

  const handleDeleteEquipment = async (equipmentId) => {
    if (!window.confirm("Are you sure you want to delete this equipment?")) {
      return;
    }

    try {
      await axios.delete(`/equipment/${equipmentId}`);
      // Update equipment list
      setEquipment(equipment.filter((item) => item._id !== equipmentId));
    } catch (err) {
      setError("Failed to delete equipment. Please try again.");
    }
    closeEquipmentMenu();
  };

  const handleViewImage = (imageUrl) => {
    window.open(`/api/uploads/${imageUrl}`, "_blank");
    closeEquipmentMenu();
  };

  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), "MMMM dd, yyyy");
    } catch (error) {
      return "Invalid Date";
    }
  };

  const exportToCsv = async () => {
    if (!equipment || equipment.length === 0) {
      setError("No equipment data to export");
      return;
    }

    try {
      const response = await axios.get(`/equipment/job/${jobId}/export`, {
        responseType: "blob",
      });

      // Create a download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `job_${jobId}_equipment.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      setError("Failed to export equipment data");
      console.error("Error exporting CSV:", error);
    }
  };

  const getStatusChip = (status) => {
    let color = "default";

    switch (status) {
      case "pending":
        color = "warning";
        break;
      case "in-progress":
        color = "info";
        break;
      case "completed":
        color = "success";
        break;
      default:
        color = "default";
    }

    return (
      <Chip
        label={status.toUpperCase()}
        color={color}
        size="small"
        sx={{ textTransform: "capitalize" }}
      />
    );
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1 }}>
      <AppBar position="static">
        <Toolbar>
          <IconButton
            size="large"
            edge="start"
            color="inherit"
            sx={{ mr: 2 }}
            onClick={goBack}
          >
            <BackIcon />
          </IconButton>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Job Details
          </Typography>
          <Button
            color="inherit"
            startIcon={<EditIcon />}
            onClick={handleEditJob}
          >
            Edit
          </Button>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {job && (
          <>
            <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={8}>
                  <Typography variant="h5" gutterBottom>
                    {job.name}
                  </Typography>
                  <Box sx={{ mt: 2 }}>
                    <Typography
                      variant="body1"
                      color="text.secondary"
                      gutterBottom
                    >
                      <strong>Date:</strong> {formatDate(job.date)}
                    </Typography>
                    <Typography
                      variant="body1"
                      color="text.secondary"
                      gutterBottom
                    >
                      <strong>Address:</strong> {job.address}
                    </Typography>
                    {job.description && (
                      <Typography
                        variant="body1"
                        color="text.secondary"
                        gutterBottom
                      >
                        <strong>Description:</strong> {job.description}
                      </Typography>
                    )}
                  </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body1" gutterBottom>
                      <strong>Status:</strong> {getStatusChip(job.status)}
                    </Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body1" gutterBottom>
                      <strong>Assigned To:</strong>{" "}
                      {job.assignedTo ? job.assignedTo.name : "Unassigned"}
                    </Typography>
                  </Box>
                  <Button
                    variant="contained"
                    startIcon={<AssignIcon />}
                    onClick={() => navigate(`/admin/job/${jobId}`)}
                    sx={{ width: "100%", mb: 2 }}
                  >
                    Reassign Job
                  </Button>
                </Grid>
              </Grid>
            </Paper>

            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Typography variant="h6">
                Equipment ({equipment.length})
              </Typography>
              {equipment.length > 0 && (
                <Button
                  variant="contained"
                  startIcon={<DownloadIcon />}
                  onClick={exportToCsv}
                >
                  Export CSV
                </Button>
              )}
            </Box>

            {equipment.length === 0 ? (
              <Alert severity="info">
                No equipment has been captured for this job yet.
              </Alert>
            ) : (
              <Grid container spacing={2}>
                {equipment.map((item) => (
                  <Grid item xs={12} sm={6} md={3} key={item._id}>
                    <Card
                      sx={{
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      <CardContent sx={{ flexGrow: 1, padding: 2 }}>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "flex-start",
                            marginBottom: 1,
                          }}
                        >
                          <Typography
                            variant="h6"
                            gutterBottom
                            sx={{
                              width: "calc(100% - 40px)",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {item.equipmentType || "Unknown Type"}
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={(e) => openEquipmentMenu(e, item)}
                          >
                            <MoreVertIcon />
                          </IconButton>
                        </Box>

                        <Divider sx={{ my: 1.5 }} />

                        <List dense disablePadding>
                          <ListItem sx={{ py: 0.5 }}>
                            <ListItemText
                              primary="Brand"
                              secondary={item.brand || "Not detected"}
                              primaryTypographyProps={{
                                variant: "body2",
                                sx: { fontWeight: 500 },
                              }}
                              secondaryTypographyProps={{
                                variant: "body2",
                                sx: {
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                },
                              }}
                            />
                          </ListItem>
                          <ListItem sx={{ py: 0.5 }}>
                            <ListItemText
                              primary="Serial Number"
                              secondary={item.serialNumber || "Not detected"}
                              primaryTypographyProps={{
                                variant: "body2",
                                sx: { fontWeight: 500 },
                              }}
                              secondaryTypographyProps={{
                                variant: "body2",
                                sx: {
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                },
                              }}
                            />
                          </ListItem>
                          <ListItem sx={{ py: 0.5 }}>
                            <ListItemText
                              primary="Model Number"
                              secondary={item.modelNumber || "Not detected"}
                              primaryTypographyProps={{
                                variant: "body2",
                                sx: { fontWeight: 500 },
                              }}
                              secondaryTypographyProps={{
                                variant: "body2",
                                sx: {
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                },
                              }}
                            />
                          </ListItem>
                          <ListItem sx={{ py: 0.5 }}>
                            <ListItemText
                              primary="Manufactured Date"
                              secondary={
                                item.manufacturedDate || "Not detected"
                              }
                              primaryTypographyProps={{
                                variant: "body2",
                                sx: { fontWeight: 500 },
                              }}
                              secondaryTypographyProps={{
                                variant: "body2",
                                sx: {
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                },
                              }}
                            />
                          </ListItem>
                        </List>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
          </>
        )}
      </Container>

      {/* Equipment Actions Menu */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={closeEquipmentMenu}
      >
        {selectedEquipment?.images?.length > 0 && (
          <MenuItem
            onClick={() => handleViewImage(selectedEquipment.images[0])}
          >
            <ListItemIcon>
              <ViewIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="View Image" />
          </MenuItem>
        )}
        <MenuItem onClick={() => handleDeleteEquipment(selectedEquipment?._id)}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText
            primary="Delete Equipment"
            sx={{ color: "error.main" }}
          />
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default JobDetailsView;
